import React, { useState } from 'react';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Avatar,
  User,
} from '@heroui/react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import VerificationEmailModal from './VerificationEmailModal';

const UserMenu = () => {
  const { currentUser, userProfile, logout, sendVerificationEmail } = useAuth();
  const navigate = useNavigate();
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationError, setVerificationError] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
    }
  };

  const handleSendVerification = async () => {
    if (sendingEmail) return;

    try {
      setSendingEmail(true);
      setVerificationError(false);
      await sendVerificationEmail();
      setShowVerificationModal(true);
    } catch (error) {
      setVerificationError(true);
      setShowVerificationModal(true);
    } finally {
      setSendingEmail(false);
    }
  };

  if (!currentUser || !userProfile) {
    return null;
  }

  // Backend returns full CDN URL in photo_url (same as comments)
  const avatarSrc = userProfile.photo_url || null;
  const avatarName = userProfile.display_name || userProfile.username;

  return (
    <>
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          <Avatar
            as="button"
            className="transition-transform hover:scale-105"
            src={avatarSrc}
            name={avatarName}
            size="sm"
            isBordered
            color="primary"
          />
        </DropdownTrigger>
        <DropdownMenu aria-label="User menu actions" variant="flat">
          <DropdownItem key="profile" className="h-14 gap-2" textValue="Profile">
            <User
              name={avatarName}
              description={`@${userProfile.username}`}
              classNames={{
                name: "text-sm font-semibold",
                description: "text-xs text-gray-500"
              }}
              avatarProps={{
                size: "sm",
                src: avatarSrc,
                name: avatarName,
              }}
            />
          </DropdownItem>
          <DropdownItem
            key="my_profile"
            startContent={<UserCircleIcon className="w-5 h-5" />}
            onPress={() => navigate('/profile')}
          >
            My Profile
          </DropdownItem>
          {!currentUser.emailVerified && (
            <DropdownItem
              key="verify_email"
              startContent={<EnvelopeIcon className="w-5 h-5" />}
              onPress={handleSendVerification}
              className="text-warning"
              description="Email not verified"
            >
              {sendingEmail ? 'Sending...' : 'Verify Email'}
            </DropdownItem>
          )}
          <DropdownItem
            key="logout"
            color="danger"
            startContent={<ArrowRightOnRectangleIcon className="w-5 h-5" />}
            onPress={handleLogout}
          >
            Log Out
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      {/* Verification Email Modal */}
      <VerificationEmailModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        isError={verificationError}
      />
    </>
  );
};

export default UserMenu;
